import { Modal } from 'stories/components'

import { Appraisal2StatusType } from '../types'

export type RefreshResultChange = {
  field: string
  from: string
  to: string
}

export type RefreshResultOrder = {
  id: number | string
  vendor?: string
  changes?: RefreshResultChange[]
  error?: string
}

export type RefreshResultsSummary = {
  total?: number
  skipped?: number
  orders?: RefreshResultOrder[]
}

const refreshFieldTitles: Record<string, string> = {
  status: 'Status',
  inspectionScheduled: 'Inspection Scheduled',
  inspectionCompleted: 'Inspection Completed',
  clientDueDate: 'Client Due Date',
  appraisalCost: 'Appraisal Cost',
  paymentStatus: 'Payment Status',
  acceptedDate: 'Accepted Date',
  completeDate: 'Completed Date',
  paidDate: 'Paid Date',
  vendorStatus: 'Vendor Status',
  paymentLink: 'Payment Link',
}

const refreshFieldTitle = (field: string) => refreshFieldTitles[field] || field

const formatRefreshValue = (field: string, value: string) => {
  if (!value) return '—'
  if (field === 'status') return Appraisal2StatusType[value] || value
  if (field === 'appraisalCost' && !`${value}`.startsWith('$')) return `$${value}`
  return value
}

export const RefreshResults = ({
  summary,
  lastUpdatedAt,
  onClose,
}: {
  summary: RefreshResultsSummary | null
  lastUpdatedAt: number
  onClose: () => void
}) => {
  return (
    <Modal
      button={<span></span>}
      title="Refresh results"
      titleOkay=""
      titleCancel="Close"
      isOpen={!!summary}
      lastUpdatedAt={lastUpdatedAt}
      onClose={onClose}
    >
      <div className="w-[calc(100vw-5rem)] max-w-[760px] text-sm">
        <p className="mb-3 text-gray-600">
          Checked {summary?.total || 0} vendor order(s).
          {summary?.skipped ? ` ${summary.skipped} order(s) have no vendor order to check.` : ''}
        </p>
        {Array.isArray(summary?.orders) && summary && summary.orders.length > 0 ? (
          <div className="max-h-[60vh] overflow-auto rounded border">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2">Order</th>
                  <th className="px-2 py-2">Vendor</th>
                  <th className="px-2 py-2">Field</th>
                  <th className="px-2 py-2">From</th>
                  <th className="px-2 py-2">To</th>
                </tr>
              </thead>
              <tbody>
                {summary?.orders.flatMap((order) => {
                  const rows = Array.isArray(order.changes) ? order.changes : []
                  if (!rows.length) {
                    return [
                      <tr key={`${order.id}-error`} className="border-t">
                        <td className="px-2 py-2">{order.id}</td>
                        <td className="px-2 py-2">{order.vendor || '—'}</td>
                        <td className="px-2 py-2 text-red-700" colSpan={3}>
                          {order.error || 'No fields changed'}
                        </td>
                      </tr>,
                    ]
                  }
                  return rows.map((change, index) => (
                    <tr key={`${order.id}-${change.field}-${index}`} className="border-t">
                      <td className="px-2 py-2">{index === 0 ? order.id : ''}</td>
                      <td className="px-2 py-2">
                        {index === 0 ? order.vendor || '—' : ''}
                        {index === 0 && order.error ? <div className="text-xs text-red-700">{order.error}</div> : null}
                      </td>
                      <td className="px-2 py-2">
                        {refreshFieldTitle(change.field)}
                        {change.field === 'vendorStatus' ? (
                          <div className="text-xs text-amber-700">Not mapped to a Nexys status</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">{formatRefreshValue(change.field, change.from)}</td>
                      <td className="px-2 py-2">{formatRefreshValue(change.field, change.to)}</td>
                    </tr>
                  ))
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No order fields changed.</p>
        )}
      </div>
    </Modal>
  )
}
